import React from 'react';

import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';

import { useGooglePlacesPredictions } from '../../../hooks/useGooglePlacesPredictions';
import { felixAutocompleteDropdownStyles } from '../../../shared/commonStyles';

import type { FC, HTMLAttributes, SyntheticEvent } from 'react';

import type { GooglePlacesAutocompleteProps } from './types';

export const GooglePlacesAutocomplete: FC<GooglePlacesAutocompleteProps> = ({
  disabled = false,
  fullWidth = true,
  inputId,
  onKeyDown,
  onPlaceSelect,
  onValueChange,
  placeholder = 'Search for a place...',
  size = 'small',
  value,
  textFieldSx
}) => {
  const {
    predictions,
    loading: loadingPredictions,
    fetchPredictions
  } = useGooglePlacesPredictions();

  const handleInputChange = (_: SyntheticEvent, newInputValue: string) => {
    onValueChange(newInputValue);

    if (!newInputValue.trim()) {
      return;
    }

    fetchPredictions(newInputValue);
  };

  const handleOptionSelect = (
    _: SyntheticEvent,
    option: string | google.maps.places.AutocompletePrediction | null
  ) => {
    if (option && typeof option === 'object' && onPlaceSelect) {
      onPlaceSelect(option);
    }
  };

  const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: google.maps.places.AutocompletePrediction
  ) => (
    <Box component="li" {...props}>
      <Box>
        <Box sx={{ fontWeight: 'bold' }}>
          {option.structured_formatting?.main_text ?? option.description}
        </Box>
        {option.structured_formatting?.secondary_text ? (
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {option.structured_formatting.secondary_text}
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  return (
    <Autocomplete
      freeSolo
      disableClearable={!value?.trim()}
      disabled={disabled}
      fullWidth={fullWidth}
      loading={loadingPredictions}
      onChange={handleOptionSelect}
      onInputChange={handleInputChange}
      options={predictions}
      renderOption={renderOption}
      size={size}
      slotProps={{ paper: { sx: felixAutocompleteDropdownStyles } }}
      value={value}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.description ?? '';
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          disabled={disabled}
          id={inputId}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          sx={textFieldSx}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingPredictions ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            },
            htmlInput: {
              ...params.inputProps,
              id: inputId
            }
          }}
        />
      )}
    />
  );
};
